import { useSyncExternalStore } from 'react'
import { DEFAULT_UI_SIZE, UI_SIZE_CHOICES, type UiSize } from '@workflow/ui'

export type Theme = 'system' | 'light' | 'dark'

export interface Appearance {
  theme: Theme
  uiSize: UiSize
  /** The drifting light pools behind the page. Quiet rooms for quiet eyes. */
  ambient: boolean
}

const STORAGE_KEY = 'ops.appearance'
const DEFAULTS: Appearance = { theme: 'system', uiSize: DEFAULT_UI_SIZE, ambient: true }

/** A per-browser preference. If storage is unavailable the defaults apply. */
function load(): Appearance {
  try {
    const stored = JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? 'null') as Partial<Appearance> | null
    return {
      theme: stored?.theme === 'light' || stored?.theme === 'dark' ? stored.theme : 'system',
      uiSize: UI_SIZE_CHOICES.includes(stored?.uiSize as UiSize) ? (stored!.uiSize as UiSize) : DEFAULT_UI_SIZE,
      ambient: stored?.ambient !== false,
    }
  } catch {
    return DEFAULTS
  }
}

const media = window.matchMedia('(prefers-color-scheme: dark)')
let current = load()
const listeners = new Set<() => void>()

/** Writes the preference to the root. Called before first paint and on every change. */
export function applyAppearance(appearance: Appearance = current) {
  const root = document.documentElement
  const dark = appearance.theme === 'dark' || (appearance.theme === 'system' && media.matches)
  root.classList.toggle('dark', dark)
  root.style.setProperty('--ui-size', `${appearance.uiSize}px`)
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', dark ? '#1f1f1f' : '#f9f9f9')
}

media.addEventListener('change', () => {
  if (current.theme === 'system') applyAppearance()
})

export function setAppearance(patch: Partial<Appearance>) {
  current = { ...current, ...patch }
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(current))
  } catch {
    // Storage is blocked. The change still applies for this session.
  }
  applyAppearance()
  listeners.forEach((listener) => listener())
}

export function useAppearance(): Appearance {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
    () => current,
  )
}
